import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_ALERT_TYPES = [
  {
    id: 'cliente_operando_sin_otorgamiento',
    label: 'Cliente operando sin otorgamiento',
    description: 'Marcado como operando en gestión cartera, sin otorgamientos en el mes.',
    count: 0,
    rows: [],
  },
  {
    id: 'en_gestion_arrastrado',
    label: 'En gestión arrastrado',
    description: 'Campaña del mes anterior cerrada con clientes aún en En gestión (gestión incompleta).',
    count: 0,
    rows: [],
  },
  {
    id: 'compromete_ope_sin_operar',
    label: 'Compromete operación y no operó',
    description: 'Campaña del mes anterior en Compromete operación para el mes, sin otorgamientos al cierre.',
    count: 0,
    rows: [],
  },
];

const MESES_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const fecIniCamMes = (row) => {
  if (row?.fec_ini_cam_mes) return row.fec_ini_cam_mes;
  const raw = row?.fec_ini_cam;
  if (!raw) return '-';
  const d = new Date(`${String(raw).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '-';
  return MESES_ES[d.getMonth()] || '-';
};

const SeguimientoEjecutivoAlertasModal = ({
  open,
  onClose,
  alertTypes = DEFAULT_ALERT_TYPES,
  loading = false,
  error = '',
  month = '',
}) => {
  const [activeTabId, setActiveTabId] = useState('cliente_operando_sin_otorgamiento');
  const [search, setSearch] = useState('');

  const tabs = alertTypes.length > 0 ? alertTypes : DEFAULT_ALERT_TYPES;

  const activeTab = useMemo(
    () => tabs.find((t) => t.id === activeTabId) || tabs[0],
    [tabs, activeTabId],
  );

  const filteredRows = useMemo(() => {
    const rows = activeTab?.rows || [];
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => (
      `${r.rut} ${r.cliente} ${r.ejecutivo} ${r.plataforma} ${r.responsable} ${r.estado} ${r.fec_ini_cam_mes} ${r.fec_ini_cam} ${r.fec_estado_mes} ${r.fec_estado}`
    ).toLowerCase().includes(q));
  }, [activeTab?.rows, search]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;
    setSearch('');
    setActiveTabId('cliente_operando_sin_otorgamiento');
    document.addEventListener('keydown', handleKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return createPortal(
    <div
      className="ps-modal-overlay"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="ps-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ps-alerts-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ps-modal-header">
          <div>
            <h2 id="ps-alerts-modal-title">Alertas de seguimiento</h2>
            {month && <p className="ps-modal-subtitle">Periodo: {month}</p>}
          </div>
          <button type="button" className="ps-modal-close" onClick={onClose} aria-label="Cerrar">
            ×
          </button>
        </header>

        <div className="ps-modal-body">
          <nav className="ps-modal-tabs" aria-label="Tipos de alerta">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`ps-modal-tab ${activeTab?.id === tab.id ? 'active' : ''}`}
                onClick={() => {
                  setActiveTabId(tab.id);
                  setSearch('');
                }}
              >
                <span>{tab.label}</span>
                <span className="ps-modal-tab-count">{tab.count ?? 0}</span>
              </button>
            ))}
          </nav>

          <section className="ps-modal-content">
            {activeTab?.description && (
              <p className="ps-modal-description">{activeTab.description}</p>
            )}

            <div className="ps-modal-toolbar">
              <input
                className="ps-table-search"
                type="search"
                placeholder="Buscar en alertas..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={loading}
              />
              <span className="ps-modal-result-count">
                {filteredRows.length} registro{filteredRows.length === 1 ? '' : 's'}
              </span>
            </div>

            {loading && (
              <div className="ps-modal-state">Cargando alertas...</div>
            )}

            {!loading && error && (
              <div className="ps-alert-error">{error}</div>
            )}

            {!loading && !error && filteredRows.length === 0 && (
              <div className="ps-modal-state">Sin alertas para este periodo.</div>
            )}

            {!loading && !error && filteredRows.length > 0 && (
              <div className="ps-modal-table-wrap">
                <table className="ps-modal-table">
                  <thead>
                    <tr>
                      <th>RUT</th>
                      <th>Cliente</th>
                      <th>Ejecutivo</th>
                      <th>Plataforma</th>
                      <th>Responsable</th>
                      <th>Estado</th>
                      <th>Mes Campaña</th>
                      {(activeTab?.id === 'en_gestion_arrastrado' || activeTab?.id === 'compromete_ope_sin_operar') && (
                        <>
                          <th>Mes estado</th>
                          <th>Cierre campaña</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => (
                      <tr key={`${row.rut}-${row.fec_ini_cam}-${row.responsable}`}>
                        <td>{row.rut}</td>
                        <td>{row.cliente}</td>
                        <td>{row.ejecutivo}</td>
                        <td>{row.plataforma}</td>
                        <td>{row.responsable}</td>
                        <td>{row.estado}</td>
                        <td>{fecIniCamMes(row)}</td>
                        {(activeTab?.id === 'en_gestion_arrastrado' || activeTab?.id === 'compromete_ope_sin_operar') && (
                          <>
                            <td>{row.fec_estado_mes || '-'}</td>
                            <td>{row.fec_fin_cam_mes || '-'}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SeguimientoEjecutivoAlertasModal;
